/**
 * PlanChangeCard - Shared component for upgrade/downgrade plan change cards
 *
 * Used in subscription.tsx to render both the "Upgrade to DramaX" and
 * "Switch to Drama+" cards with consistent structure but different styling.
 */
import { View, Text, Pressable, ActivityIndicator } from "react-native";
import { GlassCard } from "../../../components/glass";

export interface PlanChangeCardProps {
  type: 'upgrade' | 'downgrade';
  currentPrice: string;
  targetPrice: string;
  currentLabel: string;
  targetLabel: string;
  title: string;
  description: string;
  ctaLabel: string;
  isLoading: boolean;
  onPress: () => void;
}

export function PlanChangeCard({
  type,
  currentPrice,
  targetPrice,
  currentLabel,
  targetLabel,
  title,
  description,
  ctaLabel,
  isLoading,
  onPress,
}: PlanChangeCardProps) {
  const isUpgrade = type === 'upgrade';

  return (
    <GlassCard>
      <View className="py-1">
        <Text
          className={`${isUpgrade ? 'text-lg font-bold text-purple-900' : 'text-base font-semibold text-gray-700'} mb-1`}
        >
          {title}
        </Text>
        {isUpgrade ? (
          <Text className="text-xs text-green-600 mb-2">Effective immediately</Text>
        ) : (
          <Text className="text-xs text-gray-500 mb-2">Effective at end of billing period</Text>
        )}
        <Text className={`text-sm ${isUpgrade ? 'text-gray-600' : 'text-gray-500'} mb-3`}>
          {description}
        </Text>
        {/* Price comparison */}
        <View
          className={`flex-row items-center justify-between mb-4 ${isUpgrade ? 'bg-purple-50' : 'bg-gray-50'} rounded-lg p-3`}
        >
          <View>
            <Text className="text-xs text-gray-500">{currentLabel}</Text>
            <Text className="text-base font-semibold text-gray-700">{currentPrice}</Text>
          </View>
          <Text className="text-lg text-gray-400">{'→'}</Text>
          <View>
            <Text className={`text-xs ${isUpgrade ? 'text-purple-600' : 'text-gray-500'}`}>
              {targetLabel}
            </Text>
            <Text
              className={`text-base ${isUpgrade ? 'font-bold text-purple-700' : 'font-semibold text-gray-600'}`}
            >
              {targetPrice}
            </Text>
          </View>
        </View>
        {type === 'upgrade' && (
          <Text className="text-xs text-gray-500 mb-3">
            2,000 → 5,000 char limit · 100 → 200/day · + Narrative Clarity
          </Text>
        )}
        {type === 'downgrade' && (
          <Text className="text-xs text-gray-500 mb-3">
            5,000 → 2,000 char limit · 200 → 100/day · - Narrative Clarity
          </Text>
        )}
        <Pressable
          onPress={onPress}
          disabled={isLoading}
          className={`py-3 rounded-full items-center ${
            isLoading
              ? 'bg-gray-300'
              : isUpgrade
                ? 'bg-purple-600 active:bg-purple-700'
                : 'bg-gray-100 active:bg-gray-200'
          }`}
          accessibilityRole="button"
          accessibilityLabel={title}
          testID={isUpgrade ? 'upgrade-plan-button' : 'downgrade-plan-button'}
        >
          {isLoading ? (
            <ActivityIndicator color={isUpgrade ? 'white' : '#6B7280'} size="small" />
          ) : (
            <Text
              className={`text-base ${isUpgrade ? 'text-white font-bold' : 'text-gray-700 font-medium'}`}
            >
              {ctaLabel}
            </Text>
          )}
        </Pressable>
      </View>
    </GlassCard>
  );
}
